import { useQuery } from "@apollo/client/react";
import { type FC, useMemo, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Help, LoadingIndicator } from "src/components/fragments";
import PerformerCard from "src/components/performerCard";
import PerformerSelect from "src/components/performerSelect";
import {
  FindPerformersDocument,
  type FullPerformerQuery,
  OperationEnum,
  type PerformerEditDetailsInput,
  type SearchPerformersQuery,
  usePerformerEdit,
} from "src/graphql";
import { editHref } from "src/utils";
import PerformerForm from "./performerForm";
import { buildPerformerMerge } from "./performerForm/merge";

type Performer = NonNullable<FullPerformerQuery["findPerformer"]>;
type SearchPerformer = NonNullable<
  SearchPerformersQuery["searchPerformers"]["performers"][number]
>;

const UPDATE_ALIAS_MESSAGE = `Enabling this option sets each merged performer's name as an alias on every scene that performer does not have an alias on.
In most cases, it should be enabled when merging aliases of a performer, and disabled when the performers share the same name.
`;

const CLASSNAME = "PerformerMerge";

interface Props {
  performer: Performer;
}

const PerformerMerge: FC<Props> = ({ performer }) => {
  const navigate = useNavigate();
  const [submissionError, setSubmissionError] = useState("");
  const [mergeActive, setMergeActive] = useState(false);
  const [mergeSources, setMergeSources] = useState<SearchPerformer[]>([]);
  const [aliasUpdating, setAliasUpdating] = useState(true);

  const {
    data: sourcesData,
    loading: sourcesLoading,
    error: sourcesError,
  } = useQuery(FindPerformersDocument, {
    variables: { ids: mergeSources.map((source) => source.id) },
    skip: !mergeActive || mergeSources.length === 0,
  });
  const loadedSources = (sourcesData?.findPerformers ?? []).filter(
    (source) => source != null,
  );
  const sourcesReady =
    !sourcesLoading && loadedSources.length === mergeSources.length;

  const [insertPerformerEdit, { loading: saving }] = usePerformerEdit({
    onCompleted: (data) => {
      if (submissionError) setSubmissionError("");
      if (data.performerEdit.id) navigate(editHref(data.performerEdit));
    },
    onError: (error) => setSubmissionError(error.message),
  });

  const toggleMerge = () => {
    setMergeActive(true);
    const sameName = mergeSources.every(
      ({ name }) => name.trim() === performer.name.trim(),
    );
    // Don't update aliases by default if the names match
    setAliasUpdating(!sameName);
  };

  const doUpdate = (
    insertData: PerformerEditDetailsInput,
    editNote: string,
    setModifyAliases: boolean,
  ) => {
    insertPerformerEdit({
      variables: {
        performerData: {
          edit: {
            id: performer.id,
            operation: OperationEnum.MERGE,
            merge_source_ids: mergeSources.map((p) => p.id),
            comment: editNote,
          },
          details: insertData,
          options: {
            set_merge_aliases: aliasUpdating,
            set_modify_aliases: setModifyAliases,
          },
        },
      },
    });
  };

  const { initial, conflicts } = useMemo(
    () => buildPerformerMerge(performer, loadedSources),
    [performer, loadedSources],
  );

  return (
    <div className={CLASSNAME}>
      <h3>
        Merge performers into <em>{performer.name}</em>
      </h3>
      <hr />
      <div className="row">
        <div className="col-6">
          {!mergeActive && (
            <>
              <label
                htmlFor="performer-merge-source-select"
                className="form-label"
              >
                Merge sources
              </label>
              <PerformerSelect
                performers={[]}
                onChange={(performers) => setMergeSources(performers)}
                message="Search for performers to merge..."
                excludePerformers={[
                  performer.id,
                  ...mergeSources.map((p) => p.id),
                ]}
                inputId="performer-merge-source-select"
              />
              {mergeSources.length > 0 && (
                <Button onClick={toggleMerge} className="ms-auto">
                  Continue
                </Button>
              )}
            </>
          )}
          {mergeActive && (
            <Row>
              <Col xs={3}>
                <h6 className="text-center">Merge Target</h6>
                <PerformerCard performer={performer} className="TargetCard" />
              </Col>
              <Col xs={9}>
                <Row className="mt-4">
                  {mergeSources.map((source) => (
                    <Col xs={4} key={source.id}>
                      <PerformerCard performer={source} />
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
          )}
        </div>
        <div className="col-6">
          <p>
            Merging performers reassigns all scene performances of the sources
            to the target performer. The source <i>stashIds</i> will be
            redirected so that previously tagged content can be updated with the
            new performers metadata.
          </p>
          <p>
            This operation is not easily reversible and attention should be paid
            that all entities are truly the same.
          </p>
        </div>
      </div>
      {mergeActive && (
        <>
          <Form.Check
            id="merge-alias-updating"
            checked={aliasUpdating}
            onChange={() => setAliasUpdating(!aliasUpdating)}
            label="Update scene performance aliases on merged performers to old performer name."
            className="d-inline-block"
          />
          <Help message={UPDATE_ALIAS_MESSAGE} />
          <h5 className="mt-4">
            Update performer metadata for <em>{performer.name}</em>
          </h5>
          {sourcesError ? (
            <div className="text-danger">
              Failed to load performer details: {sourcesError.message}
            </div>
          ) : sourcesReady ? (
            <PerformerForm
              performer={performer}
              initial={initial}
              conflicts={conflicts}
              callback={doUpdate}
              saving={saving}
            />
          ) : (
            <LoadingIndicator message="Loading performer details..." />
          )}
          {submissionError && (
            <div className="text-danger text-end col-9">
              Error: {submissionError}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PerformerMerge;
